import api from '../config/api';
import axios from 'axios';

/**
 * Customers Service
 * Handles customer list and creation requests.
 */

/**
 * Get customers with pagination and optional filters.
 * @param {Object} params
 * @returns {Promise<Object>} API response payload
 */
export const getCustomers = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      search,
      customer_name,
      city,
      state,
      agency_id,
    } = params;

    const offset = (page - 1) * limit;

    const queryParams = {
      limit,
      offset,
    };

    if (search) queryParams.search = search;
    if (customer_name) queryParams.customer_name = customer_name;
    if (city) queryParams.city = city;
    if (state) queryParams.state = state;
    if (agency_id) queryParams.agency_id = agency_id;

    const response = await api.get('/api/customers', { params: queryParams });
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch customers. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get the next customer ID from the database.
 * This ensures customer IDs continue from the database, not from the frontend loaded list.
 * @returns {Promise<String>} Next customer ID (e.g., "CUST-001")
 */
export const getNextCustomerId = async () => {
  try {
    console.log('Calling /api/customers/next-id endpoint...');
    const response = await api.get('/api/customers/next-id');
    console.log('Next Customer ID API Response:', response);
    
    const data = response.data || response;
    console.log('Next Customer ID Data:', data);
    
    // Try multiple possible response formats
    const nextId = 
      data.next_id || 
      data.nextId || 
      data.customer_id || 
      data.customerId ||
      data.next_customer_id ||
      data.nextCustomerId ||
      data.data?.next_id ||
      data.data?.nextId ||
      (typeof data === 'string' ? data : null);
    
    if (nextId) {
      console.log('✅ Next Customer ID from API:', nextId);
      return nextId;
    } else {
      console.warn('⚠️ Next Customer ID not found in response, response structure:', JSON.stringify(data, null, 2));
      throw new Error('Invalid response format from next-id endpoint');
    }
  } catch (error) {
    console.error('❌ Error fetching next customer ID from API:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url
    });
    
    // Check if it's a 404 (endpoint doesn't exist)
    if (error.response?.status === 404) {
      console.warn('⚠️ /api/customers/next-id endpoint not found (404). Using fallback method.');
    }
    
    // If endpoint doesn't exist, fallback to fetching all customers and calculating
    console.warn('Next customer ID endpoint not available, using fallback method');
    try {
      // Fetch customers in batches (backend limit is 100)
      let allCustomers = [];
      let page = 1;
      const limit = 100;
      let hasMore = true;
      
      while (hasMore) {
        const response = await getCustomers({ page, limit });
        let customersData = [];
        
        if (Array.isArray(response)) {
          customersData = response;
        } else if (Array.isArray(response?.data)) {
          customersData = response.data;
        } else if (Array.isArray(response?.data?.items)) {
          customersData = response.data.items;
        } else if (Array.isArray(response?.items)) {
          customersData = response.items;
        }
        
        allCustomers = allCustomers.concat(customersData);
        
        // Check if there are more pages
        const total = response?.pagination?.total || response?.meta?.total || response?.data?.pagination?.total || response?.data?.meta?.total || 0;
        hasMore = allCustomers.length < total && customersData.length === limit;
        page++;
        
        // Safety limit: don't fetch more than 10 pages (1000 customers max)
        if (page > 10) break;
      }

      console.log('Fallback: Found', allCustomers.length, 'customers to calculate next ID from');
      
      if (allCustomers.length === 0) {
        console.log('Fallback: No customers found, starting from CUST-001');
        return 'CUST-001';
      }
      
      const existingIds = allCustomers.map(c => {
        const code = c.customer_code || c.customerCode || c.customer_id || '';
        const match = code.toString().match(/CUST-(\d+)/);
        return match ? parseInt(match[1]) : 0;
      }).filter(id => id > 0); // Filter out invalid IDs
      
      if (existingIds.length === 0) {
        console.log('Fallback: No valid CUST-XXX codes found, starting from CUST-001');
        return 'CUST-001';
      }
      
      const maxId = Math.max(...existingIds);
      const nextId = maxId + 1;
      const formattedId = `CUST-${String(nextId).padStart(3, '0')}`;
      console.log('✅ Fallback: Calculated next customer ID:', formattedId, '(max found:', maxId, ')');
      return formattedId;
    } catch (fallbackError) {
      console.error('Error in fallback customer ID generation:', fallbackError);
      // Last resort: return a default ID
      console.warn('Using default customer ID: CUST-001');
      return 'CUST-001';
    }
  }
};

/**
 * Create a new customer.
 * Supports optional photo uploads via FormData.
 * @param {Object} customerData
 * @returns {Promise<Object>} Created customer payload
 */
/**
 * Get a single customer by ID.
 * @param {number} customerId - Customer ID
 * @returns {Promise<Object>} Customer data
 */
export const getCustomerById = async (customerId) => {
  try {
    const response = await api.get(`/api/customers/${customerId}`);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch customer. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update an existing customer.
 * Supports optional photo uploads via FormData.
 * @param {number} customerId - Customer ID
 * @param {Object} customerData - Customer data to update
 * @returns {Promise<Object>} Updated customer payload
 */
export const updateCustomer = async (customerId, customerData) => {
  try {
    const hasPhotoFile =
      customerData.photo && customerData.photo instanceof File;

    let payload = { ...customerData };

    if (hasPhotoFile) {
      payload = new FormData();
      Object.entries(customerData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (key === 'photo') {
            payload.append('photo', value);
          } else {
            payload.append(key, value);
          }
        }
      });
    } else if (payload.photo && !(payload.photo instanceof File)) {
      delete payload.photo;
    }

    const response = await api.put(`/api/customers/${customerId}`, payload);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to update customer. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a customer by ID.
 * @param {number|string} customerId - Customer ID (numeric database ID)
 * @returns {Promise<Object>} Deletion response
 */
export const deleteCustomer = async (customerId) => {
  try {
    const response = await api.delete(`/api/customers/${customerId}`);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to delete customer. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new customer (requires authentication).
 * Supports optional photo uploads via FormData.
 * @param {Object} customerData
 * @returns {Promise<Object>} Created customer payload
 */
export const createCustomer = async (customerData) => {
  try {
    const hasPhotoFile =
      customerData.photo && customerData.photo instanceof File;

    let payload = { ...customerData };

    if (hasPhotoFile) {
      payload = new FormData();
      Object.entries(customerData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (key === 'photo') {
            payload.append('photo', value);
          } else {
            payload.append(key, value);
          }
        }
      });
    } else if (payload.photo && !(payload.photo instanceof File)) {
      delete payload.photo;
    }

    const response = await api.post('/api/customers', payload);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to create customer. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new customer via public signup (no authentication required).
 * This is used for the public customer signup page.
 * Supports optional photo uploads via FormData.
 * @param {Object} customerData
 * @returns {Promise<Object>} Created customer payload
 */
export const createCustomerPublic = async (customerData) => {
  try {
    // Use plain axios instance without auth interceptor for public signup
    const publicApi = axios.create({
      baseURL: 'https://transportapi.nettaa.in',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const hasPhotoFile =
      customerData.photo && customerData.photo instanceof File;

    let payload = { ...customerData };

    // Remove undefined values to clean up payload
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    if (hasPhotoFile) {
      payload = new FormData();
      Object.entries(customerData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (key === 'photo') {
            payload.append('photo', value);
          } else {
            payload.append(key, value);
          }
        }
      });
      // Remove Content-Type for FormData to let browser set it
      delete publicApi.defaults.headers['Content-Type'];
    } else if (payload.photo && !(payload.photo instanceof File)) {
      delete payload.photo;
    }

    // Use public signup endpoint (no authentication required)
    console.log('Sending signup request:', payload);
    const response = await publicApi.post('/api/auth/signup', payload);
    console.log('Signup response:', response.data);
    return response.data || response;
  } catch (error) {
    console.error('Signup error details:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    // Extract detailed error message
    let errorMessage = 'Failed to create account. Please try again.';
    
    if (error.response?.data) {
      // Try to get detailed error message from backend
      if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    // Add status code info for debugging
    if (error.response?.status === 500) {
      errorMessage += ' (Server error - please contact support if this persists)';
    }
    
    throw new Error(errorMessage);
  }
};


